import { CdkTrapFocus, FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';
import { CdkPortalOutlet, ComponentPortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  signal,
  ViewChild,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';

import { HxTabNavigation } from '../../a11y';
import { getEditableControls } from '../../a11y/focus-utils';
import { HxDialogConfig } from './dialog-config';
import { HxDialogRef } from './dialog-ref';

/**
 * Internal container component that wraps dialog content Handles backdrop, panel styling, animations, and focus
 * management
 *
 * @internal
 */
@Component({
  selector: 'hx-dialog-container',
  standalone: true,
  imports: [CommonModule, CdkPortalOutlet, CdkTrapFocus],
  hostDirectives: [HxTabNavigation],
  templateUrl: './dialog-container.html',
  styleUrls: ['./dialog-container.css', './dialog-tokens.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'hx-dialog-container',
    '[attr.role]': '"dialog"',
    '[attr.aria-modal]': 'true',
    '[attr.aria-labelledby]': 'config()?.ariaLabelledBy',
    '[attr.aria-label]': 'config()?.ariaLabel',
    '[attr.aria-describedby]': 'config()?.ariaDescribedBy',
    '[id]': 'config()?.id',
  },
})
export class HxDialogContainerComponent implements AfterViewInit, OnDestroy {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly focusTrapFactory = inject(FocusTrapFactory);

  @ViewChild(CdkPortalOutlet, { static: true })
  portalOutlet!: CdkPortalOutlet;

  /** Configuration for this dialog instance */
  readonly config: WritableSignal<HxDialogConfig | undefined> = signal(undefined);

  /** Reference to the dialog instance */
  dialogRef: HxDialogRef | null = null;

  /** Reference to the component instance inside dialog */
  componentRef: ComponentRef<any> | null = null;

  /** Focus trap instance */
  private focusTrap: FocusTrap | null = null;

  /** Element that had focus before dialog opened */
  private previousActiveElement: HTMLElement | null = null;

  /** Check if custom dimensions are provided */
  hasCustomDimensions(): boolean {
    const cfg = this.config();
    return !!(cfg?.width || cfg?.height || cfg?.minWidth || cfg?.minHeight || cfg?.maxWidth || cfg?.maxHeight);
  }

  ngAfterViewInit(): void {
    // T061: Initialize FocusTrap
    this.focusTrap = this.focusTrapFactory.create(this.elementRef.nativeElement);

    // T063: Store previous active element
    this.previousActiveElement = document.activeElement as HTMLElement;

    // T062: Auto-focus logic (delay to ensure content is rendered)
    setTimeout(() => {
      this.autoFocus();
    }, 0);
  }

  ngOnDestroy(): void {
    // T065: Destroy focus trap
    if (this.focusTrap) {
      this.focusTrap.destroy();
    }

    // T064: Restore focus
    if (this.config()?.restoreFocus && this.previousActiveElement) {
      this.previousActiveElement.focus();
    }
  }

  /** Attach a component portal to this container */
  attachComponentPortal<T>(portal: ComponentPortal<T>): ComponentRef<T> {
    this.componentRef = this.portalOutlet.attachComponentPortal(portal);
    return this.componentRef;
  }

  /** T046: Handle backdrop click */
  onBackdropClick(event: MouseEvent): void {
    const cfg = this.config();
    if (cfg?.closeOnBackdropClick && !cfg?.disableClose && this.dialogRef) {
      this.dialogRef.close();
    }
  }

  /** T047: Handle close button click */
  onCloseClick(): void {
    if (this.dialogRef && !this.config()?.disableClose) {
      this.dialogRef.close();
    }
  }

  /** T048: Handle Escape key press */
  @HostListener('keydown.escape', ['$event'])
  onEscapeKeydown(event: Event): void {
    const cfg = this.config();
    if (cfg?.closeOnEscape && !cfg?.disableClose && this.dialogRef) {
      event.preventDefault();
      this.dialogRef.close();
    }
  }

  /** T062 & T139: Auto-focus implementation Focuses first interactive element or fallback to close button/container */
  private autoFocus(): void {
    const autoFocusConfig = this.config()?.autoFocus ?? 'first-element';

    if (autoFocusConfig === 'none') {
      return;
    }

    if (autoFocusConfig === 'close-button') {
      const closeButton = this.elementRef.nativeElement.querySelector('.hx-dialog-close-btn') as HTMLElement | null;
      if (closeButton) {
        closeButton.focus();
        return;
      }
    }

    // Focus first interactive element (excluding close button)
    const panel = this.elementRef.nativeElement.querySelector('.hx-dialog-panel');
    if (panel) {
      // Skips disabled, hidden and read-only controls so the dialog opens on a genuinely editable field.
      const focusableElements = getEditableControls(panel as HTMLElement);

      // Find first focusable element that's not the close button
      for (const element of focusableElements) {
        if (!element.classList.contains('hx-dialog-close-btn')) {
          element.focus();
          return;
        }
      }

      // Fallback to close button
      const closeButton = panel.querySelector('.hx-dialog-close-btn') as HTMLElement | null;
      if (closeButton) {
        closeButton.focus();
      } else {
        // T139: No focusable elements - add tabindex to panel
        panel.setAttribute('tabindex', '0');
        (panel as HTMLElement).focus();
      }
    }
  }
}
